import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Hook: useLocalStorage ---
// A custom hook to manage state that persists in localStorage.
// It initializes state from localStorage if available, otherwise uses an initial value.
// It also updates localStorage whenever the state changes.
function useLocalStorage(key, initialValue) {
    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.error("Error reading from localStorage:", error);
            return initialValue;
        }
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(key, JSON.stringify(storedValue));
        } catch (error) {
            console.error("Error writing to localStorage:", error);
        }
    }, [key, storedValue]);

    return [storedValue, setStoredValue];
}

// --- Data: RATIO_DEFINITIONS and categories ---
// Defines all the accounting ratios, their required inputs, calculation logic,
// formula string, and formatting for the result.
const RATIO_DEFINITIONS = {
    // --- Costing Basics & Production Costs ---
    "Total Manufacturing Cost": {
        inputs: ["Direct Materials", "Direct Labor", "Manufacturing Overhead"],
        calculate: (values) => values.reduce((sum, val) => sum + val, 0),
        formula: "Direct Materials + Direct Labor + Manufacturing Overhead",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },
    "Direct Materials Used": {
        inputs: ["Beginning Raw Materials Inventory", "Purchases of Raw Materials", "Ending Raw Materials Inventory"],
        calculate: (values) => values[0] + values[1] - values[2],
        formula: "Beginning Raw Materials Inventory + Purchases of Raw Materials - Ending Raw Materials Inventory",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },
    "Direct Labor Cost": {
        inputs: ["Direct Labor Hours", "Direct Labor Rate per Hour"],
        calculate: (values) => values[0] * values[1],
        formula: "Direct Labor Hours × Direct Labor Rate per Hour",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },
    "Cost of Goods Manufactured (COGM)": {
        inputs: ["Beginning Work-in-Process Inventory", "Total Manufacturing Cost", "Ending Work-in-Process Inventory"],
        calculate: (values) => values[0] + values[1] - values[2],
        formula: "Beginning Work-in-Process Inventory + Total Manufacturing Cost - Ending Work-in-Process Inventory",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },
    "Cost of Goods Sold (COGS)": {
        inputs: ["Beginning Finished Goods Inventory", "Cost of Goods Manufactured", "Ending Finished Goods Inventory"],
        calculate: (values) => values[0] + values[1] - values[2],
        formula: "Beginning Finished Goods Inventory + Cost of Goods Manufactured - Ending Finished Goods Inventory",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },
    "Unit Product Cost": {
        inputs: ["Total Manufacturing Cost", "Number of Units Produced"],
        calculate: (values) => values[0] / values[1],
        formula: "Total Manufacturing Cost / Number of Units Produced",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : value.toFixed(2)
    },

    // --- Profitability & Pricing ---
    "Gross Profit": {
        inputs: ["Net Sales Revenue", "Cost of Goods Sold"],
        calculate: (values) => values[0] - values[1],
        formula: "Net Sales Revenue - Cost of Goods Sold",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },
    "Gross Profit Margin": {
        inputs: ["Gross Profit", "Net Sales Revenue"],
        calculate: (values) => (values[0] / values[1]) * 100,
        formula: "(Gross Profit / Net Sales Revenue) × 100%",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}%`
    },
    "Operating Income": {
        inputs: ["Gross Profit", "Operating Expenses (Selling, General & Administrative)"],
        calculate: (values) => values[0] - values[1],
        formula: "Gross Profit - Operating Expenses",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },
    "Net Income": {
        inputs: ["Operating Income", "Interest Expense", "Taxes"],
        calculate: (values) => values[0] - values[1] - values[2],
        formula: "Operating Income - Interest Expense - Taxes",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },
    "Break-Even Point (Units)": {
        inputs: ["Total Fixed Costs", "Selling Price Per Unit", "Variable Cost Per Unit"],
        calculate: (values) => values[0] / (values[1] - values[2]),
        formula: "Total Fixed Costs / (Selling Price Per Unit - Variable Cost Per Unit)",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)} units`
    },
    "Break-Even Point (Sales Dollars)": {
        inputs: ["Total Fixed Costs", "Selling Price Per Unit", "Variable Cost Per Unit"],
        calculate: (values) => {
            const contributionMarginPerUnit = values[1] - values[2];
            if (contributionMarginPerUnit === 0) throw new Error("Contribution Margin Per Unit cannot be zero for Break-Even Point (Sales Dollars).");
            const contributionMarginRatio = contributionMarginPerUnit / values[1];
            if (contributionMarginRatio === 0) throw new Error("Contribution Margin Ratio cannot be zero for Break-Even Point (Sales Dollars).");
            return values[0] / contributionMarginRatio;
        },
        formula: "Total Fixed Costs / ((Selling Price Per Unit - Variable Cost Per Unit) / Selling Price Per Unit)",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `$${value.toFixed(2)}`
    },
    "Markup Percentage (on Cost)": {
        inputs: ["Selling Price", "Cost"],
        calculate: (values) => ((values[0] - values[1]) / values[1]) * 100,
        formula: "((Selling Price - Cost) / Cost) × 100%",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}%`
    },
    "Markup Percentage (on Selling Price)": {
        inputs: ["Selling Price", "Cost"],
        calculate: (values) => ((values[0] - values[1]) / values[0]) * 100,
        formula: "((Selling Price - Cost) / Selling Price) × 100%",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}%`
    },

    // --- Activity-Based Costing (ABC) ---
    "Cost Driver Rate": {
        inputs: ["Total Cost Pool for Activity", "Total Activity (e.g., number of setups, machine hours)"],
        calculate: (values) => values[0] / values[1],
        formula: "Total Cost Pool for Activity / Total Activity",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : value.toFixed(2)
    },
    "Allocated Overhead Cost (ABC)": {
        inputs: ["Cost Driver Rate", "Number of Cost Driver Units Used by Product/Service"],
        calculate: (values) => values[0] * values[1],
        formula: "Cost Driver Rate × Number of Cost Driver Units Used by Product/Service",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },

    // --- Process Driven Costing (PDC) / Process Costing ---
    "Equivalent Units of Production (EUP) - Materials (Weighted-Average)": {
        inputs: ["Units Completed and Transferred Out", "Ending Work-in-Process Units", "Percentage Completion for Materials (as decimal)"],
        calculate: (values) => values[0] + (values[1] * values[2]),
        formula: "Units Completed and Transferred Out + (Ending Work-in-Process Units × Percentage Completion for Materials)",
        format: (value) => isNaN(value) ? "N/A" : `${value.toFixed(2)} units`
    },
    "Equivalent Units of Production (EUP) - Conversion (Weighted-Average)": {
        inputs: ["Units Completed and Transferred Out", "Ending Work-in-Process Units", "Percentage Completion for Conversion Costs (as decimal)"],
        calculate: (values) => values[0] + (values[1] * values[2]),
        formula: "Units Completed and Transferred Out + (Ending Work-in-Process Units × Percentage Completion for Conversion Costs)",
        format: (value) => isNaN(value) ? "N/A" : `${value.toFixed(2)} units`
    },
    "Cost Per Equivalent Unit - Materials (Weighted-Average)": {
        inputs: ["Beginning WIP Materials Cost", "Current Period Materials Cost", "EUP (Materials)"],
        calculate: (values) => (values[0] + values[1]) / values[2],
        formula: "(Beginning WIP Materials Cost + Current Period Materials Cost) / EUP (Materials)",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : value.toFixed(2)
    },
    "Cost Per Equivalent Unit - Conversion (Weighted-Average)": {
        inputs: ["Beginning WIP Conversion Cost", "Current Period Conversion Cost", "EUP (Conversion)"],
        calculate: (values) => (values[0] + values[1]) / values[2],
        formula: "(Beginning WIP Conversion Cost + Current Period Conversion Cost) / EUP (Conversion)",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : value.toFixed(2)
    },

    // --- Liquidity Ratios ---
    "Current Ratio": {
        inputs: ["Current Assets", "Current Liabilities"],
        calculate: (values) => values[0] / values[1],
        formula: "Current Assets / Current Liabilities",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}:1`
    },
    "Quick Ratio": {
        inputs: ["Current Assets", "Inventory", "Current Liabilities"],
        calculate: (values) => (values[0] - values[1]) / values[2],
        formula: "(Current Assets - Inventory) / Current Liabilities",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}:1`
    },
    "Cash Ratio": {
        inputs: ["Cash", "Cash Equivalents", "Current Liabilities"],
        calculate: (values) => (values[0] + values[1]) / values[2],
        formula: "(Cash + Cash Equivalents) / Current Liabilities",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}:1`
    },

    // --- Other Profitability Ratios ---
    "Return on Assets (ROA)": {
        inputs: ["Net Income", "Average Total Assets"],
        calculate: (values) => (values[0] / values[1]) * 100,
        formula: "Net Income / Average Total Assets",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}%`
    },
    "Return on Equity (ROE)": {
        inputs: ["Net Income", "Average Shareholder Equity"],
        calculate: (values) => (values[0] / values[1]) * 100,
        formula: "Net Income / Average Shareholder Equity",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}%`
    },

    // --- Solvency Ratios ---
    "Debt-to-Equity Ratio": {
        inputs: ["Total Debt", "Shareholder Equity"],
        calculate: (values) => values[0] / values[1],
        formula: "Total Debt / Shareholder Equity",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}:1`
    },
    "Debt-to-Assets Ratio": {
        inputs: ["Total Debt", "Total Assets"],
        calculate: (values) => values[0] / values[1],
        formula: "Total Debt / Total Assets",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}`
    },
    "Interest Coverage Ratio": {
        inputs: ["Earnings Before Interest and Taxes (EBIT)", "Interest Expense"],
        calculate: (values) => values[0] / values[1],
        formula: "EBIT / Interest Expense",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : value.toFixed(2)
    },

    // --- Efficiency Ratios ---
    "Inventory Turnover Ratio": {
        inputs: ["Cost of Goods Sold", "Average Inventory"],
        calculate: (values) => values[0] / values[1],
        formula: "Cost of Goods Sold / Average Inventory",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)} times`
    },
    "Accounts Receivable Turnover Ratio": {
        inputs: ["Net Credit Sales", "Average Accounts Receivable"],
        calculate: (values) => values[0] / values[1],
        formula: "Net Credit Sales / Average Accounts Receivable",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)} times`
    },
    "Asset Turnover Ratio": {
        inputs: ["Net Sales", "Average Total Assets"],
        calculate: (values) => values[0] / values[1],
        formula: "Net Sales / Average Total Assets",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)} times`
    },
    "Accounts Payable Turnover Ratio": {
        inputs: ["Total Supplier Purchases", "Average Accounts Payable"],
        calculate: (values) => values[0] / values[1],
        formula: "Total Supplier Purchases / Average Accounts Payable",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)} times`
    },

    // --- Market Prospect Ratios ---
    "Earnings Per Share (EPS)": {
        inputs: ["Net Income", "Preferred Dividends", "Number of Common Shares Outstanding"],
        calculate: (values) => (values[0] - values[1]) / values[2],
        formula: "(Net Income - Preferred Dividends) / Number of Common Shares Outstanding",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `$${value.toFixed(2)}`
    },
    "Price-to-Earnings (P/E) Ratio": {
        inputs: ["Market Price Per Share", "Earnings Per Share"],
        calculate: (values) => values[0] / values[1],
        formula: "Market Price Per Share / Earnings Per Share",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : value.toFixed(2)
    },
    "Dividend Yield": {
        inputs: ["Annual Dividends Per Share", "Market Price Per Share"],
        calculate: (values) => (values[0] / values[1]) * 100,
        formula: "Annual Dividends Per Share / Market Price Per Share × 100%",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}%`
    },

    // --- Other Relevant & Commonly Used Calculations/KPIs ---
    "Days Inventory Outstanding (DIO)": {
        inputs: ["Average Inventory", "Cost of Goods Sold"],
        calculate: (values) => (values[0] / values[1]) * 365,
        formula: "(Average Inventory / Cost of Goods Sold) × 365 days",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)} days`
    },
    "Working Capital": {
        inputs: ["Current Assets", "Current Liabilities"],
        calculate: (values) => values[0] - values[1],
        formula: "Current Assets - Current Liabilities",
        format: (value) => isNaN(value) ? "N/A" : value.toFixed(2)
    },
    "Overall Equipment Effectiveness (OEE)": {
        inputs: ["Availability (as decimal)", "Performance (as decimal)", "Quality (as decimal)"],
        calculate: (values) => values[0] * values[1] * values[2] * 100,
        formula: "Availability × Performance × Quality × 100%",
        format: (value) => isNaN(value) ? "N/A" : `${value.toFixed(2)}%`
    },
    "Scrap Rate / Defect Rate": {
        inputs: ["Number of Scrapped Units", "Total Units Produced"],
        calculate: (values) => (values[0] / values[1]) * 100,
        formula: "(Number of Scrapped Units / Total Units Produced) × 100%",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)}%`
    },
    "Throughput": {
        inputs: ["Number of Units Produced", "Time Period (e.g., in hours)"],
        calculate: (values) => values[0] / values[1],
        formula: "Number of Units Produced / Time Period",
        format: (value) => isNaN(value) || !isFinite(value) ? "N/A" : `${value.toFixed(2)} units/time`
    }
};

// Defines the categories and the ratios belonging to each category.
const categories = {
    "Costing Basics & Production Costs": [
        "Total Manufacturing Cost",
        "Direct Materials Used",
        "Direct Labor Cost",
        "Cost of Goods Manufactured (COGM)",
        "Cost of Goods Sold (COGS)",
        "Unit Product Cost"
    ],
    "Profitability & Pricing": [
        "Gross Profit",
        "Gross Profit Margin",
        "Operating Income",
        "Net Income",
        "Break-Even Point (Units)",
        "Break-Even Point (Sales Dollars)",
        "Markup Percentage (on Cost)",
        "Markup Percentage (on Selling Price)"
    ],
    "Activity-Based Costing (ABC)": [
        "Cost Driver Rate",
        "Allocated Overhead Cost (ABC)"
    ],
    "Process Driven Costing (PDC)": [
        "Equivalent Units of Production (EUP) - Materials (Weighted-Average)",
        "Equivalent Units of Production (EUP) - Conversion (Weighted-Average)",
        "Cost Per Equivalent Unit - Materials (Weighted-Average)",
        "Cost Per Equivalent Unit - Conversion (Weighted-Average)"
    ],
    "Liquidity Ratios": [
        "Current Ratio",
        "Quick Ratio",
        "Cash Ratio"
    ],
    "Other Profitability Ratios": [
        "Return on Assets (ROA)",
        "Return on Equity (ROE)"
    ],
    "Solvency Ratios": [
        "Debt-to-Equity Ratio",
        "Debt-to-Assets Ratio",
        "Interest Coverage Ratio"
    ],
    "Efficiency Ratios": [
        "Inventory Turnover Ratio",
        "Accounts Receivable Turnover Ratio",
        "Asset Turnover Ratio",
        "Accounts Payable Turnover Ratio"
    ],
    "Market Prospect Ratios": [
        "Earnings Per Share (EPS)",
        "Price-to-Earnings (P/E) Ratio",
        "Dividend Yield"
    ],
    "Key Performance Indicators (KPIs)": [
        "Days Inventory Outstanding (DIO)",
        "Working Capital",
        "Overall Equipment Effectiveness (OEE)",
        "Scrap Rate / Defect Rate",
        "Throughput"
    ]
};

// --- Component: Header ---
// Displays the main title and a brief description of the calculator.
const Header = React.memo(() => {
    return (
        <motion.header
            className="text-center mb-10 pt-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <h1 className="text-4xl font-extrabold text-jakom-gold mb-2">
                Accounting Ratio Calculator
            </h1>
            <p className="text-jakom-light-text text-lg">
                Analyze financial health with key accounting ratios.
            </p>
        </motion.header>
    );
});

// --- Component: RatioCategorySection ---
// Renders a section for a specific category of accounting ratios.
// Each ratio is a clickable button that opens a modal for calculation.
const RatioCategorySection = React.memo(({ categoryName, ratios, onOpenModal }) => {
    return (
        <motion.section
            className="mb-8 p-6 bg-jakom-card-bg rounded-xl shadow-lg border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
        >
            <h2 className="text-2xl font-bold text-jakom-gold mb-5 border-b-2 border-jakom-gold pb-3">
                {categoryName}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ratios.map(ratioName => (
                    <motion.button
                        key={ratioName}
                        className="bg-jakom-dark-blue text-jakom-light-text font-bold py-3 px-6 rounded-lg shadow-md hover:bg-opacity-90 transition duration-300 ease-in-out transform hover:scale-105 active:bg-jakom-dark-blue focus:outline-none focus:ring-2 focus:ring-jakom-gold focus:ring-opacity-50"
                        onClick={() => onOpenModal(ratioName)}
                        aria-label={`Calculate ${ratioName}`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {ratioName}
                    </motion.button>
                ))}
            </div>
        </motion.section>
    );
});

// --- Component: RatioModal ---
// A modal component for entering inputs, calculating, and displaying the result of a selected ratio.
const RatioModal = React.memo(({ isOpen, onClose, ratio, onSaveCalculation }) => {
    const [inputValues, setInputValues] = useState({});
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const inputRefs = useRef([]); // Ref to clear input fields

    // Resets modal state when it opens or ratio changes
    useEffect(() => {
        if (isOpen && ratio) {
            setInputValues({});
            setResult(null);
            setError('');
            // Clear input fields visually
            inputRefs.current.forEach(input => {
                if (input) input.value = '';
            });
        }
    }, [isOpen, ratio]);

    // Handles changes in input fields
    const handleInputChange = (e, inputName) => {
        setResult(null); // Clear previous result on input change
        setError(''); // Clear previous error on input change
        setInputValues(prev => ({
            ...prev,
            [inputName]: e.target.value
        }));
    };

    // Performs the calculation based on current input values
    const handleCalculate = () => {
        if (!ratio) return;

        const values = ratio.definition.inputs.map(inputName => {
            const value = parseFloat(inputValues[inputName]);
            return isNaN(value) ? undefined : value;
        });

        if (values.some(val => val === undefined)) {
            setError('Please enter valid numbers for all fields.');
            setResult(null);
            return;
        }

        try {
            const calculatedResult = ratio.definition.calculate(values);
            setResult(ratio.definition.format(calculatedResult));
            setError('');
        } catch (e) {
            setError(`Calculation Error: ${e.message || 'Invalid input for calculation.'}`);
            setResult(null);
        }
    };

    // Saves the current calculation to history
    const handleSave = () => {
        if (result && !error) {
            onSaveCalculation({
                name: ratio.name,
                inputs: inputValues,
                result: result,
                date: new Date().toISOString()
            });
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose} // Close modal on overlay click
                >
                    <motion.div
                        className="bg-jakom-card-bg p-6 rounded-lg shadow-2xl w-full max-w-md relative"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ type: "spring", damping: 20, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
                    >
                        <button
                            className="absolute top-3 right-3 text-jakom-dark-text hover:text-jakom-gold text-2xl font-bold p-1 rounded-full hover:bg-gray-100 transition"
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            &times;
                        </button>
                        <h2 className="text-2xl font-extrabold text-jakom-dark-blue mb-4 text-center border-b-2 border-jakom-gold pb-2">
                            {ratio?.name}
                        </h2>
                        {/* CHANGE APPLIED HERE: Added text-jakom-dark-blue for formula text */}
                        <p className="text-jakom-dark-blue text-sm mb-4 text-center italic">
                            {ratio?.definition.formula}
                        </p>

                        <div id="modal-inputs">
                            {ratio?.definition.inputs.map((inputName, index) => (
                                <div key={inputName} className="mb-4">
                                    <label htmlFor={`input-${index}`} className="block text-jakom-dark-text text-sm font-semibold mb-2">
                                        {inputName}:
                                    </label>
                                    <input
                                        type="number"
                                        id={`input-${index}`}
                                        ref={el => inputRefs.current[index] = el}
                                        className="shadow-sm appearance-none border rounded-lg w-full py-2 px-3 text-jakom-dark-text leading-tight focus:outline-none focus:ring-2 focus:ring-jakom-gold focus:border-transparent transition duration-200 ease-in-out placeholder-gray-500"
                                        placeholder={`Enter ${inputName.toLowerCase()}`}
                                        min="0"
                                        onChange={(e) => handleInputChange(e, inputName)}
                                        aria-label={`Input for ${inputName}`}
                                    />
                                </div>
                            ))}
                        </div>

                        {error && (
                            <motion.div
                                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-sm"
                                role="alert"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <span className="font-semibold">Error:</span> <span id="error-message">{error}</span>
                            </motion.div>
                        )}

                        <div className="flex justify-between space-x-3 mt-6">
                            <button
                                className="flex-1 bg-jakom-dark-blue hover:bg-jakom-dark-blue/90 text-jakom-light-text font-bold py-3 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-jakom-gold focus:ring-opacity-50"
                                onClick={handleCalculate}
                            >
                                Calculate
                            </button>
                            {/* CHANGE APPLIED HERE: Added text-jakom-dark-blue for button text */}
                            <button
                                className="flex-1 bg-jakom-gold hover:bg-jakom-gold/90 text-jakom-dark-blue font-bold py-3 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-jakom-gold focus:ring-opacity-50"
                                onClick={handleSave}
                                disabled={!result || !!error}
                            >
                                Save Calculation
                            </button>
                        </div>

                        {result && (
                            <motion.div
                                className="mt-6 p-4 bg-jakom-light-blue/10 rounded-lg border border-jakom-light-blue/30 text-center text-jakom-dark-blue"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                            >
                                <h3 className="text-xl font-bold mb-2">Result:</h3>
                                <p className="text-3xl font-extrabold text-jakom-dark-blue">{result}</p>
                            </motion.div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
});

// --- Component: ConfirmationModal ---
// A generic confirmation modal for actions like clearing all history.
const ConfirmationModal = React.memo(({ isOpen, onClose, onConfirm, title, message }) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="bg-jakom-card-bg p-6 rounded-lg shadow-2xl w-full max-w-sm"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ type: "spring", damping: 20, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-xl font-bold text-jakom-dark-blue mb-4">{title}</h3>
                        <p className="text-jakom-dark-text mb-6">{message}</p>
                        <div className="flex justify-end space-x-4">
                            <button
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg shadow-sm transition duration-300 ease-in-out"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-red-500 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition duration-300 ease-in-out"
                                onClick={() => { onConfirm(); onClose(); }}
                            >
                                Confirm
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
});

// --- Component: CalculationsHistory ---
// Displays a list of saved calculations, with options to export and clear.
const CalculationsHistory = React.memo(({ calculations, onExportCsv, onClearAll, onDeleteCalculation }) => {
    return (
        <section className="mt-10 p-6 bg-jakom-card-bg rounded-xl shadow-lg border border-gray-100">
            <h2 className="text-2xl font-bold text-jakom-gold mb-5 border-b-2 border-jakom-gold pb-3 flex justify-between items-center">
                Calculated Ratios History
                <div className="flex space-x-2">
                    <button
                        className="bg-jakom-gold hover:bg-jakom-gold/90 text-jakom-dark-blue font-semibold py-2 px-4 rounded-lg shadow-sm text-sm transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-jakom-gold focus:ring-opacity-50"
                        onClick={onExportCsv}
                        disabled={calculations.length === 0}
                        aria-label="Export calculations to CSV"
                    >
                        Export to CSV
                    </button>
                    <button
                        className="bg-red-500 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm text-sm transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                        onClick={onClearAll}
                        disabled={calculations.length === 0}
                        aria-label="Clear all calculations"
                    >
                        Clear All
                    </button>
                </div>
            </h2>
            <div id="calculations-history">
                <AnimatePresence mode="popLayout">
                    {calculations.length === 0 ? (
                        <motion.p
                            className="text-jakom-dark-text text-center py-8"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            No calculations saved yet. Click a ratio button to get started!
                        </motion.p>
                    ) : (
                        <ul className="space-y-4">
                            {calculations.map((calc, index) => (
                                <motion.li
                                    key={calc.date + index} // Unique key for animation
                                    className="bg-jakom-light-bg p-4 rounded-lg shadow flex flex-col sm:flex-row justify-between items-start sm:items-center"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="flex-1 mb-2 sm:mb-0">
                                        {/* Adjusted colors for better visibility */}
                                        <p className="font-bold text-lg text-jakom-gold">{calc.name}</p>
                                        <p className="text-base text-jakom-dark-blue">Result: <span className="font-semibold">{calc.result}</span></p>
                                        <p className="text-xs text-gray-600">
                                            {new Date(calc.date).toLocaleString()}
                                        </p>
                                    </div>
                                    <button
                                        className="bg-red-500 hover:bg-red-700 text-white font-semibold py-2 px-3 rounded-lg shadow-sm text-sm transition duration-300 ease-in-out transform hover:scale-105"
                                        onClick={() => onDeleteCalculation(index)}
                                        aria-label={`Delete calculation for ${calc.name}`}
                                    >
                                        Delete
                                    </button>
                                </motion.li>
                            ))}
                        </ul>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
});

// --- Main Component: AccountingCalculatorPage ---
// This is the main page component that orchestrates all other components
// and manages the overall state of the calculator.
const AccountingCalculatorPage = () => {
    // State for managing the modal and selected ratio
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRatio, setSelectedRatio] = useState(null);

    // State for managing saved calculations using localStorage hook
    const [savedCalculations, setSavedCalculations] = useLocalStorage('accountingCalculations', []);

    // State for confirmation modal
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');

    // Function to open the ratio calculation modal
    const openModal = useCallback((ratioName) => {
        setSelectedRatio({
            name: ratioName,
            definition: RATIO_DEFINITIONS[ratioName]
        });
        setIsModalOpen(true);
    }, []);

    // Function to close the ratio calculation modal
    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedRatio(null);
    }, []);

    // Function to save a new calculation to history
    const saveCalculation = useCallback((newCalculation) => {
        setSavedCalculations(prevCalculations => [newCalculation, ...prevCalculations]);
    }, [setSavedCalculations]);

    // Function to delete a specific calculation from history
    const deleteCalculation = useCallback((indexToDelete) => {
        setSavedCalculations(prevCalculations =>
            prevCalculations.filter((_, index) => index !== indexToDelete)
        );
    }, [setSavedCalculations]);

    // Function to export all calculations to a CSV file
    const exportCsv = useCallback(() => {
        if (savedCalculations.length === 0) return;

        const headers = ["Ratio Name", "Result", "Date", "Inputs"];
        const rows = savedCalculations.map(calc => {
            const inputsString = Object.entries(calc.inputs)
                .map(([key, value]) => `${key}: ${value}`)
                .join('; ');
            return [
                calc.name,
                calc.result,
                new Date(calc.date).toLocaleString(),
                inputsString
            ];
        });

        let csvContent = headers.join(",") + "\n";
        rows.forEach(rowArray => {
            csvContent += rowArray.map(item => `"${item}"`).join(",") + "\n";
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', 'accounting_calculations.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [savedCalculations]);

    // Function to clear all saved calculations, with confirmation
    const clearAllCalculations = useCallback(() => {
        setConfirmTitle('Clear All History?');
        setConfirmMessage('Are you sure you want to clear all saved accounting calculations? This action cannot be undone.');
        setConfirmAction(() => () => setSavedCalculations([]));
        setIsConfirmModalOpen(true);
    }, [setSavedCalculations]);

    // Tailwind custom colors based on the provided image
    // These colors are defined directly in the style tag for simplicity.
    const tailwindColors = `
        .text-jakom-gold { color: #C9B072; } /* Gold from the image */
        .bg-jakom-dark-blue { background-color: #0A1128; } /* Dark blue from the image */
        .text-jakom-light-text { color: #F8F8F8; } /* White/Off-white for main text */
        .bg-jakom-card-bg { background-color: #FFFFFF; } /* White for card backgrounds */
        .text-jakom-dark-text { color: #333333; } /* Dark text for cards */
        .bg-jakom-light-bg { background-color: #F0F0F0; } /* Light gray for history items */
        .border-jakom-gold { border-color: #C9B072; } /* Gold border accent */
        .focus\\:ring-jakom-gold:focus { --tw-ring-color: #C9B072; } /* Focus ring for gold */
        .bg-jakom-light-blue\\/10 { background-color: rgba(10, 17, 40, 0.1); } /* Light blue tint for results */
        .border-jakom-light-blue\\/30 { border-color: rgba(10, 17, 40, 0.3); } /* Border for results */
    `;

    return (
        <div className="min-h-screen bg-jakom-dark-blue text-jakom-light-text font-sans antialiased p-4">
            {/* Custom Tailwind Colors */}
            <style>{tailwindColors}</style>

            <div className="container mx-auto max-w-4xl py-8">
                <Header />

                {/* Iterate through categories and render RatioCategorySection for each */}
                {Object.entries(categories).map(([categoryName, ratios]) => (
                    <RatioCategorySection
                        key={categoryName}
                        categoryName={categoryName}
                        ratios={ratios}
                        onOpenModal={openModal}
                    />
                ))}

                <CalculationsHistory
                    calculations={savedCalculations}
                    onExportCsv={exportCsv}
                    onClearAll={clearAllCalculations}
                    onDeleteCalculation={deleteCalculation}
                />
            </div>

            <RatioModal
                isOpen={isModalOpen}
                onClose={closeModal}
                ratio={selectedRatio}
                onSaveCalculation={saveCalculation}
            />

            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={confirmAction}
                title={confirmTitle}
                message={confirmMessage}
            />
        </div>
    );
};

export default AccountingCalculatorPage;